import { Colors, InlineEditableText, Text } from '@amelio/ui-library';

import { AgendaOpeningStatementHint } from './AgendaOpeningStatementHint';
import { AgendaOpeningStatementTitle } from './AgendaOpeningStatementTitle';
import { AGENDA_OPENING_STATEMENT_MAX_LENGTH } from '../agendaLimits';

import type { ReactNode } from 'react';

import { useAmelioTranslations } from '@/hooks';

interface AgendaOpeningStatementInlineProps {
    value: string;
    /** Read-only agenda (a completed meeting): the statement reads as a record, with nothing to type
     * into and a sentence in place of an empty statement. */
    disabled: boolean;
    onSave: (value: string) => void;
}

const renderStatement = (children: ReactNode) => (
    <Text
        size={'default'}
        color={Colors.BASE_500}
    >
        {children}
    </Text>
);

/**
 * The opening statement edited in place: its text, or its pencil, turns into a field, the way the
 * meeting's title does. A title one step above the statement's text, and the hint that hands the
 * statement over to the manager, since the text starts as the one the template offered.
 */
export const AgendaOpeningStatementInline = ({
    value,
    disabled,
    onSave,
}: AgendaOpeningStatementInlineProps) => {
    const { t } = useAmelioTranslations();

    return (
        <div
            className={'mt-6'}
            data-testid={'agenda-opening-statement'}
        >
            <div className={'mb-2 flex items-center gap-4'}>
                <AgendaOpeningStatementTitle size={'large'} />
            </div>

            <div className={'pl-6'}>
                {disabled ? (
                    renderStatement(value || t('meetingAgenda.readOnly.noOpeningStatement'))
                ) : (
                    <InlineEditableText
                        value={value}
                        onSave={onSave}
                        multiline
                        maxLength={AGENDA_OPENING_STATEMENT_MAX_LENGTH}
                        placeholder={t('meetingAgenda.openingStatementPlaceholder')}
                        editLabel={t('meetingAgenda.editOpeningStatement')}
                        fieldLabel={t('meetingAgenda.openingStatement')}
                        renderDisplay={renderStatement}
                        dataTestId={'opening-statement'}
                    />
                )}
            </div>

            {!disabled && <AgendaOpeningStatementHint />}
        </div>
    );
};
